import { Message } from 'types';

/**
 * Transform raw message data from database to Message interface
 */
export const transformMessage = (msg: any): Message => {
  const content = msg.content;
  let agent_output: any;

  // Parse structured content from assistant messages
  if (typeof content === 'object' && content.message && content.agent_output) {
    agent_output = content.agent_output;
  }

  return {
    id: msg.id,
    conversation_id: msg.conversation_id,
    role: msg.role,
    content: msg.agent_type ? content : formatMessageContent(content),
    agent_type: msg.agent_type,
    agent_output,
    agent_run_id: msg.agent_run_id,
    created_at: msg.created_at,
  };
};

/**
 * Format message content from various formats to string
 */
export const formatMessageContent = (content: any): string => {
  if (typeof content === 'object' && content.message) {
    return content.message;
  }
  if (typeof content === 'string') {
    return content;
  }
  return JSON.stringify(content);
};

/**
 * Create a temporary user message for immediate UI display
 */
export const createTempUserMessage = (content: string, conversationId?: string | null): Message => {
  return {
    id: Date.now().toString(),
    conversation_id: conversationId || '',
    role: 'user',
    content,
    created_at: new Date().toISOString(),
  };
};

/**
 * Create an error message for display
 */
export const createErrorMessage = (
  conversationId?: string | null,
  errorMessage?: string,
): Message => {
  return {
    id: Date.now().toString(),
    conversation_id: conversationId || '',
    role: 'assistant',
    content: errorMessage || 'Sorry, there was an error processing your request. Please try again.',
    created_at: new Date().toISOString(),
  };
};

/**
 * Detect agent type based on the structure of agent_output
 */
export const detectAgentType = (agentOutput: any): string => {
  if (agentOutput.posts) return 'linkedin_agent';
  if (agentOutput.tweets) return 'twitter_agent';
  if (agentOutput.title && agentOutput.sections) return 'newsletter_agent';
  if (agentOutput.headline && agentOutput.body) return 'press_release_agent';
  if (agentOutput.strategy_overview) return 'marketing_strategist_agent';
  if (agentOutput.primary_keywords) return 'seo_keyword_agent';
  if (agentOutput.query || agentOutput.query_url || agentOutput.response_text)
    return 'seo_content_agent';
  return 'unknown_agent';
};

/**
 * Extract text content for copying from agent_output
 */
export const extractTextContent = (agentOutput: any): string => {
  if (!agentOutput) return '';

  // LinkedIn posts
  if (agentOutput.posts) {
    return agentOutput.posts.map((p: any) => p.content).join('\n\n');
  }

  // Twitter tweets
  if (agentOutput.tweets) {
    return agentOutput.tweets.map((t: any) => t.content).join('\n\n');
  }

  // Newsletter content
  if (agentOutput.content) {
    return agentOutput.content;
  }

  // Press release body
  if (agentOutput.body) {
    return agentOutput.body;
  }

  // Strategy overview
  if (agentOutput.strategy_overview) {
    return agentOutput.strategy_overview;
  }

  // SEO Content Analysis
  if (agentOutput.response_text) {
    return agentOutput.response_text;
  }

  // For complex outputs, format as readable text
  return JSON.stringify(agentOutput, null, 2);
};

/**
 * Transform an array of messages using transformMessage
 */
export const transformMessages = (messages: any[]): Message[] => {
  return messages?.map(transformMessage) || [];
};
